import React, { useState } from 'react';
import { X, MapPin, Globe, Phone, Mail, Clock, Plus, Check, AlertTriangle, ExternalLink } from 'lucide-react';
import { Business } from '../types';
import { isAuthenticated } from '../services/osmApi';

interface AddBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (business: Omit<Business, 'id'>) => void;
  onOSMSubmit?: (business: Omit<Business, 'id'>) => void;
}

const categories = [
  { id: 'restaurant', label: 'Restaurant/Cafe', icon: '🍽️' },
  { id: 'tech', label: 'Technology', icon: '💻' },
  { id: 'retail', label: 'Retail/Shop', icon: '🛒' },
  { id: 'service', label: 'Services', icon: '🔧' },
  { id: 'hotel', label: 'Hotel/Lodging', icon: '🏨' },
  { id: 'entertainment', label: 'Entertainment', icon: '🎮' },
];

const paymentMethods = [
  'Direct Wallet',
  'QR Code',
  'Payment Gateway',
  'Mobile App',
  'Escrow Service',
  'Smart Contracts',
  'Lightning Network',
  'Contactless',
];

export const AddBusinessModal: React.FC<AddBusinessModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onOSMSubmit
}) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'restaurant',
    description: '',
    address: '',
    website: '',
    phone: '',
    email: '',
    openHours: '',
    xmrPaymentMethods: [] as string[],
    coordinates: { lat: 0, lng: 0 }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [submissionType, setSubmissionType] = useState<'local' | 'osm'>('local');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Geocode address if coordinates are not set
      if (formData.coordinates.lat === 0 && formData.coordinates.lng === 0 && formData.address) {
        const coords = await geocodeAddress(formData.address);
        if (coords) {
          formData.coordinates = coords;
        }
      }

      const business: Omit<Business, 'id'> = {
        ...formData,
        isOnline: true,
        osmType: 'node' as const,
      };

      if (submissionType === 'osm' && onOSMSubmit) {
        await onOSMSubmit(business);
      } else {
        await onSubmit(business);
      }
      
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error submitting business:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'restaurant',
      description: '',
      address: '',
      website: '',
      phone: '',
      email: '',
      openHours: '',
      xmrPaymentMethods: [],
      coordinates: { lat: 0, lng: 0 }
    });
    setStep(1);
    setSubmissionType('local');
  };

  const handlePaymentMethodToggle = (method: string) => {
    setFormData(prev => ({
      ...prev,
      xmrPaymentMethods: prev.xmrPaymentMethods.includes(method)
        ? prev.xmrPaymentMethods.filter(m => m !== method)
        : [...prev.xmrPaymentMethods, method]
    }));
  };

  const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const data = await response.json();
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  };

  const osmAuthenticated = isAuthenticated();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-700 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Add XMR Business</h2>
                <p className="text-gray-400 text-sm">Step {step} of 4</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Submission Type</h3>
              
              <div className="space-y-4">
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    submissionType === 'local'
                      ? 'bg-blue-500/20 border-blue-500'
                      : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  }`}
                  onClick={() => setSubmissionType('local')}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-sm">📱</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Add to Local Directory</h4>
                      <p className="text-gray-400 text-sm mt-1">
                        Business appears immediately in this app only. Quick and easy.
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded">Instant</span>
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">No Account Required</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    submissionType === 'osm'
                      ? 'bg-green-500/20 border-green-500'
                      : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  } ${!osmAuthenticated ? 'opacity-60' : ''}`}
                  onClick={() => osmAuthenticated && setSubmissionType('osm')}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-sm">🌍</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium">Add to OpenStreetMap</h4>
                      <p className="text-gray-400 text-sm mt-1">
                        Business becomes part of the global OpenStreetMap database. Available to everyone worldwide.
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded">Global</span>
                        <span className="px-2 py-1 bg-orange-500/20 text-orange-300 text-xs rounded">OSM Account Required</span>
                        {!osmAuthenticated && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-300 text-xs rounded">Not Authenticated</span>
                        )}
                      </div>
                      {!osmAuthenticated && (
                        <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs">
                          <div className="flex items-center gap-2 text-yellow-300">
                            <AlertTriangle className="w-3 h-3" />
                            <span>You need to authenticate with OpenStreetMap first</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="text-white font-medium mb-2">Understanding the Difference</h4>
                <div className="space-y-2 text-sm text-gray-400">
                  <p><strong className="text-blue-300">Local Directory:</strong> Your business appears only in this XMR Business Directory app. Perfect for testing or private listings.</p>
                  <p><strong className="text-green-300">OpenStreetMap:</strong> Your business becomes part of the global map database used by millions. It will appear in this app AND other OSM-based services worldwide.</p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter business name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: category.id }))}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        formData.category === category.id
                          ? 'bg-orange-500/20 border-orange-500 text-orange-300'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span className="text-xl">{category.icon}</span>
                      <span className="font-medium text-sm">{category.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  rows={3}
                  placeholder="Brief description of your business"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Location & Contact</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Address *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Full business address"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Website
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="+1-555-0123"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="contact@business.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Opening Hours
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.openHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, openHours: e.target.value }))}
                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Mon-Fri 9:00 AM - 6:00 PM"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">XMR Payment Methods</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-4">
                  Select all XMR payment methods you accept *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {paymentMethods.map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => handlePaymentMethodToggle(method)}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        formData.xmrPaymentMethods.includes(method)
                          ? 'bg-orange-500/20 border-orange-500 text-orange-300'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span className="font-medium">{method}</span>
                      {formData.xmrPaymentMethods.includes(method) && (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  ))}
                </div>
                {formData.xmrPaymentMethods.length === 0 && (
                  <p className="text-red-400 text-sm mt-2">
                    Please select at least one payment method
                  </p>
                )}
              </div>

              {submissionType === 'osm' && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs">🌍</span>
                    </div>
                    <div>
                      <h4 className="text-green-300 font-medium">OpenStreetMap Submission</h4>
                      <p className="text-green-200/80 text-sm mt-1">
                        This business will be added to the global OpenStreetMap database with proper XMR payment tags. 
                        It will be available to everyone worldwide and appear in various mapping services.
                      </p>
                      <a
                        href="https://wiki.openstreetmap.org/wiki/Key:payment"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 text-xs mt-2 transition-colors"
                      >
                        Learn about OSM payment tags
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-6 border-t border-gray-700">
            <div className="flex gap-3">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Previous
                </button>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              
              {step < 4 ? (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1) ||
                    (step === 2 && (!formData.name || !formData.category)) ||
                    (step === 3 && !formData.address)
                  }
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || formData.xmrPaymentMethods.length === 0 || (submissionType === 'osm' && !osmAuthenticated)}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {submissionType === 'osm' ? 'Adding to OSM...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      {submissionType === 'osm' ? 'Add to OpenStreetMap' : 'Add to Directory'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};